import { setInvoiceDetailsData, resetInvoiceDetailsData } from '../../../redux/invoiceDetailsDataSlice';
import { setSuccessfullModifyDialog } from '../../../redux/successfullModifyDialogSlice';
import MachinesConcernee from '../../machinesConcernee/machinesConcernee';
import { SuccessfullModifyDialog } from '../../SuccessfullModifyDialog';
import { CustomErrorLogger } from '../../../utils/CustomErrorLogger';
import { useHasPermission } from '../../../hooks/useHasPermission';
import React, { useCallback, useEffect, useState } from 'react';
import InvoiceDetailsHeader from './invoiceDetailsHeader';
import { useDispatch, useSelector } from 'react-redux';
import { Box, CircularProgress } from '@mui/material';
import PdfDocuments from '../../pdfDocuments';
import InvoiceMainData from './invoiceMainData';
import { useParams } from 'react-router-dom';
import InvoiceLines from './invoiceLines';
import { saveAs } from 'file-saver';
import axios from 'axios';

const InvoiceDetails = () => {

    const { id } = useParams();
    const dispatch = useDispatch();

    const hasUpdateInvoicePermission = useHasPermission('facture_update');
    const invoiceDetailsData = useSelector((state) => state.InvoiceDetailsData);

    const machines = invoiceDetailsData?.invoice_machine;

    const [pdfId, setPdfId] = useState(null);
    const [openDeletePDF, setOpenDeletePDF] = useState(false);

    const [loading, setLoading] = useState(true);
    const [selectedMachines, setSelectedMachines] = useState([]);
    const [openAddMachines, setOpenAddMachines] = React.useState(false);
    const [openRemoveMachine, setOpenRemoveMachine] = useState(false);
    const [allMachinesSelected, setAllMachinesSelected] = useState(false)
    const [addMachinesFilter, setAddMachinesFilter] = useState({
        selectedRegion: '',
        selectedSite: '',
        selectedPole: '',
        selectedMetier: '',
        selectedStatus: '',
        selectedType: '',
        selectedMark: '',
        debouncedSearch: '',
        selectedArchiveStatus: 'Unarchived'
    })

    const handleUpdateMachines = useCallback(async () => {
        const data = {
            machines: selectedMachines,
            allMachinesSelected,
            filters: addMachinesFilter,
        }
        try {
            const response = await axios.patch(`invoice/updateInvoiceMachine/${invoiceDetailsData?.id}`, data);
            if (response.status === 200 || response.status === 201) {
                dispatch(setSuccessfullModifyDialog({ open: true, type: "Modifier les machines de la facture" }));
                dispatch(setInvoiceDetailsData({ ...invoiceDetailsData, invoice_machine: response.data.invoice_machine || [] }));
                setOpenAddMachines(false);
                setSelectedMachines([]);
                setAllMachinesSelected(false);
            }
        } catch (error) {
            setLoading(false);
            console.error('Error updating machines:', error);
            CustomErrorLogger(error, "Update Machines Error in MachinesConcernee");
        }
    }, [invoiceDetailsData, dispatch, allMachinesSelected, selectedMachines, addMachinesFilter]);

    const handleRemoveMachine = useCallback(async (machine_id) => {
        const response = await axios.delete(`invoice/removeInvoiceMachine/${invoiceDetailsData?.id}/${machine_id}`);
        if (response.status === 200 || response.status === 201) {
            dispatch(setInvoiceDetailsData({
                ...invoiceDetailsData,
                invoice_machine: invoiceDetailsData?.invoice_machine.filter((machine) => machine.machine.id !== machine_id),
                // Modify invoice_detail (don't filter - just update machine lists)
                invoice_detail: invoiceDetailsData?.invoice_detail.map(detail => ({
                    ...detail,
                    // Filter out the machine from each detail's machine list
                    invoice_detail_machine: detail.invoice_detail_machine.filter(
                        idm => idm.machine.id !== machine_id
                    )
                })).filter(detail =>
                    // Only keep details that still have machines
                    detail.invoice_detail_machine.length > 0
                )
            }));
            setOpenRemoveMachine(false)
        }
    }, [invoiceDetailsData, dispatch]);

    const fetchInvoiceDetails = useCallback(async () => {
        try {
            const response = await axios.get(`invoice/${id}`);
            if ((response.status === 200 || response.status === 201) && response.data) {
                dispatch(setInvoiceDetailsData(response.data));
                setLoading(false)
            }
        } catch (error) {
            console.error('Error Fetching medical files:', error);
            CustomErrorLogger(error, "Fetch Invoice Details Error in InvoiceDetails");
        }
    }, [id, dispatch]);

    useEffect(() => {
        window.scrollTo(0, 0);
        fetchInvoiceDetails()
        return () => {
            dispatch(resetInvoiceDetailsData());
        };
    }, [fetchInvoiceDetails, id, dispatch]);

    const handleUploadDocuments = async (files) => {
        try {
            const formData = new FormData();
            Array.from(files).forEach(file => {
                if (file.type === "application/pdf") {
                    formData.append('files', file);
                } else {
                    console.error("Invalid file format");
                }
            });
            formData.append('invoice_id', invoiceDetailsData.id); // assuming invoiceDetailsData has the id

            const response = await axios.post('invoice-document', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            if (response.status === 200 || response.status === 201) {
                // Assuming the response.data contains the array of uploaded documents
                const uploadedDocuments = [...invoiceDetailsData.invoice_document, ...response.data];
                dispatch(setInvoiceDetailsData({
                    ...invoiceDetailsData,
                    invoice_document: uploadedDocuments
                }));
            }

        } catch (error) {
            console.error('Error uploading documents:', error);
            CustomErrorLogger(error, 'Error uploading documents');
        }
    };

    const handleDawnloadAllDocument = async () => {
        try {
            const response = await axios.get(`invoice-document/downloadAll/${invoiceDetailsData.id}`, { responseType: 'blob' });
            if (response.status === 200 || response.status === 201) {
                const filename = response.headers['content-disposition']
                    ?.split('filename=')[1]
                    ?.replace(/"/g, '')
                    || `invoice-${invoiceDetailsData.id}-documents.zip`;

                saveAs(new Blob([response.data]), filename);
            }
        } catch (error) {
            console.error('Error dawnloading document:', error);
            CustomErrorLogger(error, 'Error dawnloading document');
        }
    };

    const handleDawnloadDocument = async (doc) => {
        try {
            const response = await axios.get(`invoice-document/${doc.id}`, { responseType: 'blob' });
            if (response.status === 200 || response.status === 201) {
                const blob = new Blob([response.data], { type: response.headers['content-type'] });
                saveAs(blob, doc.name);
            }
        } catch (error) {
            console.error('Error dawnloading document:', error);
            CustomErrorLogger(error, 'Error dawnloading document');
        }
    };

    const handleRemoveAllDocument = async () => {
        try {
            const response = await axios.delete(`invoice-document/deleteAll/${invoiceDetailsData.id}`);

            if (response.status === 200 || response.status === 201) {
                dispatch(setInvoiceDetailsData({
                    ...invoiceDetailsData,
                    invoice_document: []
                }));
                setOpenDeletePDF(false);
            }

        } catch (error) {
            console.error('Error deleting document:', error);
            CustomErrorLogger(error, 'Error deleting document');
        }
    };

    const handleRemoveDocument = async (id) => {
        try {
            const response = await axios.delete(`invoice-document/${id}`);

            if (response.status === 200 || response.status === 201) {
                dispatch(setInvoiceDetailsData({
                    ...invoiceDetailsData,
                    invoice_document: invoiceDetailsData.invoice_document.filter(doc => doc.id !== id)
                }));
                setOpenDeletePDF(false);
            }

        } catch (error) {
            console.error('Error deleting document:', error);
            CustomErrorLogger(error, 'Error deleting document');
        }
    };

    return (
        <Box>
            {loading ?
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '80vh',
                    }}
                >
                    <CircularProgress
                        sx={{
                            '& .MuiCircularProgress-circle': { stroke: '#EA5E00' },
                        }}
                    />
                </Box>
                :
                <Box>
                    <InvoiceDetailsHeader />
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '40px',
                            width: '100%',
                            backgroundColor: 'white',
                            borderRadius: '8px',
                            padding: '20px',
                            margin: '30px 0px 30px 0px',
                        }}>
                        <InvoiceMainData />
                        <PdfDocuments
                            documents={invoiceDetailsData?.invoice_document}
                            hasUpdatePermission={hasUpdateInvoicePermission}
                            handleUploadDocuments={handleUploadDocuments}
                            handleDawnloadAllDocument={handleDawnloadAllDocument}
                            handleDawnloadDocument={handleDawnloadDocument}
                            handleRemoveAllDocument={handleRemoveAllDocument}
                            handleRemoveDocument={handleRemoveDocument}
                            openDeletePDF={openDeletePDF}
                            setOpenDeletePDF={setOpenDeletePDF}
                            pdfId={pdfId}
                            setPdfId={setPdfId}
                        />
                        <MachinesConcernee
                            machines={machines}
                            openAddMachines={openAddMachines}
                            setOpenAddMachines={setOpenAddMachines}
                            openRemoveMachine={openRemoveMachine}
                            setOpenRemoveMachine={setOpenRemoveMachine}
                            selectedMachines={selectedMachines}
                            setSelectedMachines={setSelectedMachines}
                            allMachinesSelected={allMachinesSelected}
                            setAllMachinesSelected={setAllMachinesSelected}
                            addMachinesFilter={addMachinesFilter}
                            setAddMachinesFilter={setAddMachinesFilter}
                            handleUpdateMachines={handleUpdateMachines}
                            handleRemoveMachine={handleRemoveMachine}
                            hasPermission={hasUpdateInvoicePermission}
                        />
                        <InvoiceLines />
                    </Box>
                    <SuccessfullModifyDialog />
                </Box>
            }
        </Box>
    );
}

export default InvoiceDetails;